import {
  AdjustmentsOutlineIcon,
  BookOpenOutlineIcon,
  CalendarOutlineIcon,
  CheckCircleOutlineIcon,
  ClipboardCheckOutlineIcon,
  ClockOutlineIcon,
  DocumentTextOutlineIcon,
  GavelOutlineIcon,
  GridCheckOutlineIcon,
  HierarchyOutlineIcon,
  IntegrationOutlineIcon,
  PanelUploadOutlineIcon,
  SearchOutlineIcon,
  ServerOutlineIcon,
  ShuffleOutlineIcon,
  SignOutlineIcon,
  SparklesOutlineIcon,
  SpeedometerOutlineIcon,
  SwitchPathOutlineIcon,
  WebcamOutlineIcon,
  XCircleOutlineIcon,
} from '@fountain/ripple/icon';
import { StageType } from 'api-clients/monolith';

export type ExtraStageType =
  | 'EmailPdfStage'
  | 'FilterStage'
  | 'JumpStage'
  | 'MixedStage'
  | 'TraitifyStage'
  | 'WotcStage';

export type StageIconSurface =
  | 'workflowEditor'
  | 'aiBuilder'
  | 'assessmentHeader'
  | 'informationPanel'
  | 'drawerHeader'
  | 'messengerHeader'
  | 'jobsStagesFilter'
  | 'workflowFilter'
  | 'stageCarousel'
  | 'printModal';

export const STAGE_ICON_SIZE_BY_SURFACE: Record<StageIconSurface, 'sm' | 'lg'> =
  {
    workflowEditor: 'lg',
    aiBuilder: 'sm',
    assessmentHeader: 'sm',
    informationPanel: 'sm',
    drawerHeader: 'sm',
    messengerHeader: 'sm',
    jobsStagesFilter: 'sm',
    workflowFilter: 'sm',
    stageCarousel: 'sm',
    printModal: 'sm',
  };

export interface StageIconProps {
  type?: string;
  shortType?: string;
  surface: StageIconSurface;
  isSelected?: boolean;
  className?: string;
}

export const STAGE_KEY_ICONS = {
  'ai-interview': SparklesOutlineIcon,
  archived: ClockOutlineIcon,
  assessment: ClipboardCheckOutlineIcon,
  'background-check': GavelOutlineIcon,
  custom: AdjustmentsOutlineIcon,
  'data-collection': GridCheckOutlineIcon,
  form: DocumentTextOutlineIcon,
  hired: CheckCircleOutlineIcon,
  integration: IntegrationOutlineIcon,
  'job-selector': HierarchyOutlineIcon,
  'job-switcher': SwitchPathOutlineIcon,
  learning: BookOpenOutlineIcon,
  multi: ServerOutlineIcon,
  partner: PanelUploadOutlineIcon,
  rejected: XCircleOutlineIcon,
  review: SearchOutlineIcon,
  rule: ShuffleOutlineIcon,
  scheduler: CalendarOutlineIcon,
  signing: SignOutlineIcon,
  'tech-check': SpeedometerOutlineIcon,
  'video-recording': WebcamOutlineIcon,
};

export type StageIconKey = keyof typeof STAGE_KEY_ICONS;

export const TYPE_TO_KEY: Record<
  StageType | ExtraStageType,
  StageIconKey | null
> = {
  AccurateStage: 'background-check',
  ArchivedStage: 'archived',
  AssessmentStage: 'assessment',
  BackgroundCheckerStage: 'background-check',
  'CheckrCanada::Stage': 'background-check',
  ComplianceStage: 'data-collection',
  CustomStage: 'custom',
  DataCollectionStage: 'data-collection',
  DistributeApplicantsRuleStage: 'rule',
  DocumentSignatureStage: 'signing',
  DocumentSigningStage: 'signing',
  FountainDocumentSigningStage: 'signing',
  EventStage: 'scheduler',
  HiredStage: 'hired',
  I9FormStage: 'form',
  JobSelectorStage: 'job-selector',
  JobSwitcherStage: 'job-switcher',
  LearningStage: 'learning',
  MultiStage: 'multi',
  'NewCheckrCanada::Stage': 'background-check',
  OnfidoStage: 'background-check',
  PartnerStage: 'integration',
  RehireCheckStage: 'data-collection',
  RejectedStage: 'rejected',
  ReviewStage: 'review',
  RuleStage: 'rule',
  SchedulerStage: 'scheduler',
  SchedulerV2Stage: 'scheduler',
  SchoolkeepStage: 'learning',
  SterlingStage: 'background-check',
  TechCheckStage: 'tech-check',
  VideoRecordingStage: 'video-recording',
  W4FederalFormStage: 'form',
  W4StateFormStage: 'form',
  EmailPdfStage: null,
  FilterStage: null,
  JumpStage: null,
  MixedStage: null,
  TraitifyStage: null,
  WotcStage: null,
};

const SHORT_TYPE_TO_KEY: Record<string, StageIconKey | null> = {
  'accurate bg check': 'background-check',
  'bg check': 'background-check',
  'bg check (ca)': 'background-check',
  'onfido bg check': 'background-check',
  'sterling bg check': 'background-check',
  'on hold': 'archived',
  assessment: 'assessment',
  custom: 'custom',
  data: 'data-collection',
  compliance: 'data-collection',
  'rehire check': 'data-collection',
  'distribute rule': 'rule',
  rule: 'rule',
  'doc sign': 'signing',
  'document signing': 'signing',
  signature: 'signing',
  event: 'scheduler',
  scheduler: 'scheduler',
  'scheduler v2': 'scheduler',
  approved: 'hired',
  hired: 'hired',
  i9: 'form',
  w4f: 'form',
  w4s: 'form',
  'job selector': 'job-selector',
  'job switcher': 'job-switcher',
  learning: 'learning',
  'northpass learning': 'learning',
  'personality assessment': 'learning',
  multistage: 'multi',
  rejected: 'rejected',
  review: 'review',
  'tech check': 'tech-check',
  'video recording': 'video-recording',
  'ai interview': 'ai-interview',
  'email a pdf': null,
  wotc: null,
  manual: null,
};

export function resolveStageIconKey({
  type,
  shortType,
}: Pick<
  StageIconProps,
  'type' | 'shortType' | 'surface'
>): StageIconKey | null {
  const alias = shortType?.trim().toLowerCase();
  if (alias === 'ai interview' && (!type || type === 'PartnerStage')) {
    return 'ai-interview';
  }
  if (type && Object.prototype.hasOwnProperty.call(TYPE_TO_KEY, type)) {
    return TYPE_TO_KEY[type as StageType | ExtraStageType];
  }
  if (alias && Object.prototype.hasOwnProperty.call(SHORT_TYPE_TO_KEY, alias)) {
    return SHORT_TYPE_TO_KEY[alias];
  }
  return !type && alias ? 'integration' : null;
}
